import { useState } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import CartDrawer from '../home/components/CartDrawer';
import SEO from '../../components/SEO';

export default function BlogPage() {
  const [isCartOpen, setIsCartOpen] = useState(false);

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://bodhira.online';

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${siteUrl}/blog`,
      url: `${siteUrl}/blog`,
      name: 'Blog – Mindful Living & Yoga Insights | Bodhira',
      description: 'Explore articles on yin yoga, mindful living, natural wellness, and the art of slowing down. Written by yoga instructor Taylor Schwiering.',
      breadcrumb: {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
          { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
        ],
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      '@id': `${siteUrl}/blog#blog`,
      name: 'Bodhira Blog – Mindful Living & Yoga Insights',
      description: 'Insights on mindful living, yin yoga practice, botanical wellness, and the art of presence.',
      url: `${siteUrl}/blog`,
      publisher: {
        '@type': 'Organization',
        name: 'Bodhira',
        url: siteUrl,
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/favicon.ico`,
        },
      },
      blogPost: [
        {
          '@type': 'BlogPosting',
          headline: 'Yin Yoga: History, Practice & Benefits',
          description: 'Discover the ancient practice of stillness and how Yin Yoga can transform your relationship with movement, breath, and inner peace.',
          url: `${siteUrl}/blog`,
          datePublished: '2026-03-01',
          dateModified: '2026-06-01',
          author: {
            '@type': 'Person',
            name: 'Taylor Schwiering',
            jobTitle: 'Yoga Instructor & Founder',
            url: `${siteUrl}/about`,
          },
          publisher: {
            '@type': 'Organization',
            name: 'Bodhira',
            url: siteUrl,
          },
          mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': `${siteUrl}/blog`,
          },
          keywords: 'yin yoga, mindful living, yoga practice, wellness, meditation',
          articleSection: 'Yoga & Wellness',
          timeRequired: 'PT5M',
          image: {
            '@type': 'ImageObject',
            url: 'https://static.readdy.ai/image/5489f2d2f138577c10c646e092453b29/fd7889f82fb0f8d7f7f93e7f88e31140.jpeg',
            width: 1200,
            height: 400,
          },
        },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'What is Yin Yoga?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yin Yoga is a slow-paced style of yoga that involves holding passive poses for 3–5 minutes, targeting the body\'s connective tissues such as fascia, ligaments, and joints. It draws from Traditional Chinese Medicine and is designed to stimulate the flow of chi through the body\'s meridian pathways.',
          },
        },
        {
          '@type': 'Question',
          name: 'What are the benefits of Yin Yoga?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yin Yoga offers improved flexibility and joint mobility, deep stress relief by activating the parasympathetic nervous system, enhanced mindfulness, better sleep quality, energetic balance through meridian stimulation, and emotional release through deep tissue work.',
          },
        },
        {
          '@type': 'Question',
          name: 'Who is Yin Yoga suitable for?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Yin Yoga is suitable for beginners, athletes seeking recovery, those dealing with stress or anxiety, experienced yogis looking to deepen their practice, and anyone seeking restoration in body, mind, and spirit.',
          },
        },
        {
          '@type': 'Question',
          name: 'How is Yin Yoga different from other yoga styles?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Unlike dynamic yang yoga styles such as Vinyasa or Ashtanga that focus on building strength and heat through flowing sequences, Yin Yoga uses long-held passive poses that target connective tissue rather than muscles, creating a cooling, calming, and meditative practice.',
          },
        },
      ],
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f6f3] to-white">
      <SEO
        title="Blog – Mindful Living & Yoga Insights | Bodhira"
        description="Explore articles on yin yoga, mindful living, natural wellness, and the art of slowing down. Written by yoga instructor Taylor Schwiering."
        canonical="/blog"
        ogTitle="Bodhira Blog – Yoga & Mindful Living"
        ogDescription="Insights on yin yoga, mindfulness, and natural botanical wellness by Taylor Schwiering."
        keywords="yin yoga blog, mindful living, yoga practice, wellness articles, meditation tips, New Jersey yoga"
        jsonLd={jsonLd}
      />
      <Navigation onCartClick={() => setIsCartOpen(true)} />
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-serif text-5xl md:text-6xl text-[#4a5d4f] mb-4">
            The Bodhira Blog
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Insights on mindful living, botanical wellness, and the art of presence
          </p>
        </div>
      </section>

      {/* Featured Article */}
      <article className="max-w-4xl mx-auto px-6 pb-20">
        {/* Article Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 text-sm text-gray-500 mb-4">
            <span className="px-3 py-1 bg-[#8b9d83]/10 text-[#4a5d4f] rounded-full">Featured</span>
            <span>•</span>
            <span>5 &nbsp;min read</span>
            <span>•</span>
            <span>March 2026</span>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-[#4a5d4f] mb-6 leading-tight">
            Yin Yoga: History, Practice & Benefits
          </h2>
          <p className="text-xl text-gray-600 leading-relaxed">
            Discover the ancient practice of stillness and how Yin Yoga can transform your relationship with movement, breath, and inner peace.
          </p>
        </div>

        {/* Featured Image */}
        <div className="mb-12 rounded-lg overflow-hidden">
          <img
            src="https://static.readdy.ai/image/5489f2d2f138577c10c646e092453b29/fd7889f82fb0f8d7f7f93e7f88e31140.jpeg"
            alt="Yin Yoga Practice"
            className="w-full h-[400px] object-cover object-top"
          />
        </div>

        {/* Article Content */}
        <div className="prose prose-lg max-w-none">
          {/* Introduction */}
          <div className="mb-12">
            <p className="text-gray-700 leading-relaxed mb-6">
              In a world that constantly demands speed and productivity, Yin Yoga offers a radical invitation: to slow down, to soften, and to simply be. This contemplative practice has become a sanctuary for those seeking balance in their busy lives, offering a counterpoint to the dynamic, fast-paced yoga styles that dominate modern studios.
            </p>
            <p className="text-gray-700 leading-relaxed">
              But Yin Yoga is far more than just "slow yoga." It's a deeply therapeutic practice rooted in ancient Chinese medicine, designed to access the deepest layers of the body and mind. Let's explore the rich history, unique principles, and profound benefits of this transformative practice.
            </p>
          </div>

          {/* History Section */}
          <div className="mb-12">
            <h3 className="font-serif text-3xl text-[#4a5d4f] mb-6">The Origins of Yin Yoga</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Yin Yoga emerged in the late 1970s through the innovative work of <strong>Paulie Zink</strong>, a martial arts champion and Taoist yoga teacher. Zink developed a practice that blended Hatha Yoga with Taoist principles and the fluid movements of Kung Fu, creating a system that honored both the active (yang) and passive (yin) aspects of movement.
            </p>
            <p className="text-gray-700 leading-relaxed mb-6">
              In the 1990s, <strong>Paul Grilley</strong> studied with Zink and began to refine the practice, focusing specifically on the long-held, passive poses that target the body's connective tissues. Grilley integrated his understanding of anatomy and the Chinese meridian system, creating a more accessible and therapeutic approach to the practice.
            </p>
            <p className="text-gray-700 leading-relaxed">
              <strong>Sarah Powers</strong> further popularized Yin Yoga in the early 2000s, adding psychological and mindfulness components to the practice. She coined the term "Yin Yoga" and emphasized its meditative qualities, helping it spread to yoga studios worldwide. Today, Yin Yoga is recognized as a distinct style that complements more active practices and serves as a powerful tool for stress relief and self-inquiry.
            </p>
          </div>

          {/* Pull Quote */}
          <div className="my-12 py-8 px-8 bg-[#8b9d83]/5 border-l-4 border-[#8b9d83] rounded-lg">
            <p className="font-serif text-2xl text-[#4a5d4f] italic leading-relaxed">
              "Yin Yoga teaches us that stillness is not passive—it's where the deepest transformation happens."
            </p>
          </div>

          {/* What Makes Yin Unique */}
          <div className="mb-12">
            <h3 className="font-serif text-3xl text-[#4a5d4f] mb-6">What Makes Yin Yoga Unique</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Unlike dynamic yoga styles that focus on building strength and heat through flowing sequences, Yin Yoga takes a fundamentally different approach:
            </p>
            
            <div className="space-y-6 mb-6">
              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8b9d83] flex items-center justify-center text-white font-semibold">1</div>
                <div>
                  <h4 className="font-semibold text-[#4a5d4f] mb-2">Long-Held Passive Poses</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Poses are typically held for 3 to 5 minutes (sometimes longer), allowing gravity to do the work while muscles remain relaxed. This extended time under gentle stress allows the deeper connective tissues to respond and adapt.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8b9d83] flex items-center justify-center text-white font-semibold">2</div>
                <div>
                  <h4 className="font-semibold text-[#4a5d4f] mb-2">Targets Connective Tissue</h4>
                  <p className="text-gray-700 leading-relaxed">
                    While active yoga works primarily with muscles, Yin Yoga targets the fascia, ligaments, and joints—the body's deeper, denser tissues that respond best to slow, sustained pressure.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8b9d83] flex items-center justify-center text-white font-semibold">3</div>
                <div>
                  <h4 className="font-semibold text-[#4a5d4f] mb-2">Stimulates Energy Meridians</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Drawing from Traditional Chinese Medicine, Yin Yoga poses are designed to stimulate the flow of chi (life force energy) through the body's meridian pathways, promoting energetic balance and vitality.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#8b9d83] flex items-center justify-center text-white font-semibold">4</div>
                <div>
                  <h4 className="font-semibold text-[#4a5d4f] mb-2">Meditative & Introspective</h4>
                  <p className="text-gray-700 leading-relaxed">
                    The stillness of Yin Yoga creates space for meditation and self-observation. Practitioners learn to sit with discomfort, observe thoughts without judgment, and cultivate patience and acceptance.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Section */}
          <div className="mb-12">
            <h3 className="font-serif text-3xl text-[#4a5d4f] mb-6">The Profound Benefits of Yin Yoga</h3>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="p-6 bg-white rounded-lg border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-heart-pulse-line text-3xl text-[#8b9d83]"></i>
                </div>
                <h4 className="font-semibold text-[#4a5d4f] mb-3">Improved Flexibility & Joint Mobility</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  By gently stressing the connective tissues, Yin Yoga increases range of motion and joint health, particularly in the hips, pelvis, and lower spine.
                </p>
              </div>

              <div className="p-6 bg-white rounded-lg border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-mental-health-line text-3xl text-[#8b9d83]"></i>
                </div>
                <h4 className="font-semibold text-[#4a5d4f] mb-3">Deep Stress Relief</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  The parasympathetic nervous system is activated during long holds, triggering the body's natural relaxation response and reducing cortisol levels.
                </p>
              </div>

              <div className="p-6 bg-white rounded-lg border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-focus-3-line text-3xl text-[#8b9d83]"></i>
                </div>
                <h4 className="font-semibold text-[#4a5d4f] mb-3">Enhanced Mindfulness</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Holding poses for extended periods cultivates present-moment awareness, teaching practitioners to observe sensations and emotions with equanimity.
                </p>
              </div>

              <div className="p-6 bg-white rounded-lg border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-moon-line text-3xl text-[#8b9d83]"></i>
                </div>
                <h4 className="font-semibold text-[#4a5d4f] mb-3">Better Sleep Quality</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  The calming nature of Yin Yoga makes it ideal for evening practice, helping to quiet the mind and prepare the body for restorative sleep.
                </p>
              </div>

              <div className="p-6 bg-white rounded-lg border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-flashlight-line text-3xl text-[#8b9d83]"></i>
                </div>
                <h4 className="font-semibold text-[#4a5d4f] mb-3">Energetic Balance</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  By working with the meridian system, Yin Yoga helps balance the flow of chi throughout the body, promoting overall vitality and well-being.
                </p>
              </div>

              <div className="p-6 bg-white rounded-lg border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center mb-4">
                  <i className="ri-emotion-line text-3xl text-[#8b9d83]"></i>
                </div>
                <h4 className="font-semibold text-[#4a5d4f] mb-3">Emotional Release</h4>
                <p className="text-gray-600 text-sm leading-relaxed">
                  Deep tissue work can release stored emotions and tension, creating space for healing and emotional processing in a safe, supported environment.
                </p>
              </div>
            </div>
          </div>

          {/* Yin vs Yang */}
          <div className="mb-12">
            <h3 className="font-serif text-3xl text-[#4a5d4f] mb-6">Yin vs. Yang: Understanding the Balance</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              In Chinese philosophy, yin and yang represent complementary forces that exist in all aspects of life. In yoga, this translates to different approaches to practice:
            </p>

            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-[#8b9d83]/10">
                    <th className="border border-gray-200 px-6 py-4 text-left font-semibold text-[#4a5d4f]">Yin Yoga</th>
                    <th className="border border-gray-200 px-6 py-4 text-left font-semibold text-[#4a5d4f]">Yang Yoga</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Passive, receptive</td>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Active, dynamic</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Long-held poses (3-5 min)</td>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Flowing sequences</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Targets connective tissue</td>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Targets muscles</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Cooling, calming</td>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Heating, energizing</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Meditative focus</td>
                    <td className="border border-gray-200 px-6 py-4 text-gray-700">Physical focus</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-gray-700 leading-relaxed">
              Most modern yoga styles—Vinyasa, Ashtanga, Power Yoga—are yang practices. Yin Yoga provides the perfect complement, creating a balanced practice that honors both effort and ease, strength and flexibility, doing and being.
            </p>
          </div>

          {/* Who It's For */}
          <div className="mb-12">
            <h3 className="font-serif text-3xl text-[#4a5d4f] mb-6">Who Is Yin Yoga For?</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              One of the beautiful aspects of Yin Yoga is its accessibility. This practice welcomes:
            </p>

            <ul className="space-y-3 mb-6">
              <li className="flex gap-3 text-gray-700">
                <i className="ri-checkbox-circle-fill text-[#8b9d83] mt-1 flex-shrink-0"></i>
                <span><strong>Beginners</strong> who want a gentle introduction to yoga without the pressure of complex sequences</span>
              </li>
              <li className="flex gap-3 text-gray-700">
                <i className="ri-checkbox-circle-fill text-[#8b9d83] mt-1 flex-shrink-0"></i>
                <span><strong>Athletes and active individuals</strong> seeking recovery, flexibility, and injury prevention</span>
              </li>
              <li className="flex gap-3 text-gray-700">
                <i className="ri-checkbox-circle-fill text-[#8b9d83] mt-1 flex-shrink-0"></i>
                <span><strong>Those dealing with stress or anxiety</strong> who need tools for nervous system regulation</span>
              </li>
              <li className="flex gap-3 text-gray-700">
                <i className="ri-checkbox-circle-fill text-[#8b9d83] mt-1 flex-shrink-0"></i>
                <span><strong>Experienced yogis</strong> looking to deepen their practice and explore stillness</span>
              </li>
              <li className="flex gap-3 text-gray-700">
                <i className="ri-checkbox-circle-fill text-[#8b9d83] mt-1 flex-shrink-0"></i>
                <span><strong>Anyone seeking restoration</strong> in body, mind, and spirit</span>
              </li>
            </ul>

            <p className="text-gray-700 leading-relaxed">
              Yin Yoga is particularly beneficial for those who spend long hours sitting, experience chronic tension, or simply crave a practice that honors rest as much as effort.
            </p>
          </div>

          {/* Connection to Bodhira */}
          <div className="mb-12 p-8 bg-gradient-to-br from-[#8b9d83]/10 to-[#f8f6f3] rounded-lg border border-[#8b9d83]/20">
            <h3 className="font-serif text-3xl text-[#4a5d4f] mb-6">Experience Yin Yoga at Metta Yoga With Taylor</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              At Bodhira, we believe that true wellness comes from honoring both the active and receptive aspects of ourselves. Yin Yoga classes at <strong>Metta Yoga NJ</strong> provide a sanctuary for deep rest and renewal.
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-center gap-3 text-gray-700">
                <i className="ri-calendar-line text-xl text-[#8b9d83]"></i>
                <span><strong>Sundays</strong> at 6:00 PM</span>
              </div>
              <div className="flex items-center gap-3 text-gray-700">
                <i className="ri-calendar-line text-xl text-[#8b9d83]"></i>
                <span><strong>Mondays</strong> at 4:30 PM</span>
              </div>
            </div>

            <p className="text-gray-700 leading-relaxed mb-6">
              Class is designed to guide you into stillness, supported by props, gentle guidance, and the calming ambiance of our botanical wellness products. We invite you to slow down, breathe deeply, and discover the transformative power of doing less.
            </p>

            <div className="flex flex-wrap gap-4">
              <a
                href="/"
                className="px-6 py-3 bg-[#4a5d4f] text-white rounded-lg hover:bg-[#3d4d42] transition-colors whitespace-nowrap cursor-pointer"
              >
                View Class Schedule
              </a>
              <a
                href="/shop"
                className="px-6 py-3 bg-white text-[#4a5d4f] border-2 border-[#4a5d4f] rounded-lg hover:bg-[#4a5d4f] hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                Shop Wellness Products
              </a>
            </div>
          </div>

          {/* Closing */}
          <div className="mb-12">
            <h3 className="font-serif text-3xl text-[#4a5d4f] mb-6">Begin Your Yin Journey</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              In a culture that glorifies busyness, Yin Yoga offers a radical act of self-care: permission to be still. It teaches us that we don't always need to push, strive, or achieve. Sometimes, the most profound growth happens when we soften, surrender, and simply allow.
            </p>
            <p className="text-gray-700 leading-relaxed">
              Whether you're seeking physical relief, emotional balance, or a deeper connection to yourself, Yin Yoga offers a path. We invite you to step onto your mat, settle into stillness, and discover what emerges when you give yourself the gift of time and space.
            </p>
          </div>
        </div>
      </article>

      {/* More Articles Section */}
      <section className="max-w-6xl mx-auto px-6 pb-20">
        <h3 className="font-serif text-3xl text-[#4a5d4f] mb-8 text-center">More from the Blog</h3>
        
        <div className="grid md:grid-cols-3 gap-8">
          {/* Coming Soon Card 1 */}
          <div className="bg-white rounded-lg overflow-hidden border border-gray-100 hover:shadow-lg transition-shadow cursor-pointer">
            <div className="h-48 bg-gradient-to-br from-[#8b9d83]/20 to-[#f8f6f3] flex items-center justify-center">
              <div className="text-center">
                <i className="ri-candle-line text-5xl text-[#8b9d83] mb-3"></i>
                <p className="text-sm text-gray-500 font-semibold">Coming Soon</p>
              </div>
            </div>
            <div className="p-6">
              <h4 className="font-serif text-xl text-[#4a5d4f] mb-3">The Art of Botanical Candle Making</h4>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Discover the ancient craft of creating hand-poured soy candles infused with pure essential oils and botanical elements.
              </p>
              <span className="text-sm text-[#8b9d83] font-semibold">Article in progress</span>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="bg-[#4a5d4f] py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-serif text-3xl text-white mb-4">Stay Connected</h3>
          <p className="text-white/90 mb-8">
            Subscribe to receive new articles, wellness tips, and exclusive offers delivered to your inbox.
          </p>
          <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
            <input
              type="email"
              name="email"
              placeholder="Your email address"
              className="flex-1 px-4 py-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-white/50"
              required
            />
            <button
              type="submit"
              className="px-6 py-3 bg-white text-[#4a5d4f] rounded-lg hover:bg-gray-100 transition-colors font-semibold whitespace-nowrap cursor-pointer"
            >
              Subscribe
            </button>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}
